import { CreateRotaPattern, HomeDto, RotaPatternResponse } from '@lmc/ts-common'
import {
  REVISIONS_QUERY_KEY,
  ROTA_PATTERN_CREATE_QUERY_KEY,
  ROTA_PATTERN_QUERY_KEY,
  ROTA_PATTERN_URL,
  VISIT_TYPES_QUERY_KEY,
  SHIFT_TYPES_QUERY_KEY,
  ROTA_GENERATED_QUERY_KEY,
  CHECK_ROTA_GENERATED_QUERY_KEY,
} from './queryConstants'
import { useMutation, useQueryClient } from 'react-query'
import { postJSON } from 'common/services/api'
import { showToast } from 'common/utils'
import { useSelector } from 'react-redux'
import {
  RotaPatternModalType,
  getRotaPatternSubmitValues,
} from '../RotaPattern/utils'

type UseRotaPatternCreateProps = {
  type?: RotaPatternModalType
  onCreateSuccess?: () => void
}

type UseRotaPatternCreateReturn = {
  createPattern: (values: CreateRotaPattern) => Promise<unknown>
  isLoadingCreate?: boolean
}

export const useRotaPatternCreate = ({
  type,
}: UseRotaPatternCreateProps): UseRotaPatternCreateReturn => {
  const queryClient = useQueryClient()

  const handleError = (err: any) => {
    showToast({
      title: err?.message || err?.error,
      type: 'error',
    })
  }

  const handleSuccess = (message: string) => {
    showToast({
      title: message,
      type: 'success',
    })
  }

  const home = useSelector(
    (state: { global: { home: HomeDto } }) => state.global.home,
  )

  // CREATE PATTERN
  const createFunction = async (
    values: CreateRotaPattern,
  ): Promise<RotaPatternResponse> => {
    const patterFinalSubmitValues = getRotaPatternSubmitValues({
      type,
      home,
      values,
    })

    return postJSON({
      url: `${ROTA_PATTERN_URL}`,
      method: 'POST',
      json: patterFinalSubmitValues,
      isRota: true,
    })
  }

  const { mutateAsync: createMutation, isLoading: isLoadingCreate } =
    useMutation<RotaPatternResponse, unknown, CreateRotaPattern, unknown>(
      [ROTA_PATTERN_CREATE_QUERY_KEY],
      createFunction,
      {
        onSuccess: (res, variables) => {
          if (res?.title) {
            handleSuccess('Pattern created successfully')

            queryClient.invalidateQueries(ROTA_PATTERN_QUERY_KEY)
            queryClient.invalidateQueries([VISIT_TYPES_QUERY_KEY])
            queryClient.invalidateQueries([SHIFT_TYPES_QUERY_KEY])
            queryClient.invalidateQueries([ROTA_GENERATED_QUERY_KEY])
            
            // Invalidate the check generated rota using the pattern's start date
            if (variables?.patternStart) {
              queryClient.invalidateQueries([CHECK_ROTA_GENERATED_QUERY_KEY, new Date(variables.patternStart)])
            }
          }
        },
        onError: (err) => handleError(err),
      },
    )

  const handleCreate = async (values: CreateRotaPattern) => {
    try {
      const result = await createMutation(values)
      return result
    } catch (err) {
      return err
    }
  }

  return {
    createPattern: handleCreate,
    isLoadingCreate,
  }
}
